// Per-building production balancing for the active hex colony.
// Hysteresis and a minimum interval prevent 99.99% / 100% switching loops.
// A single building changes state each interval, never an entire category.
import { EM2C_BUILDINGS } from "../data/em2c-buildings";

const TRACKED = ["power", "oxygen", "water", "food", "raw_water"];
const SLOWED = new Set(["oxygen", "water", "food", "raw_water", "hydrogen"]);
const UPPER = 0.84;
const LOWER = 0.68;
const RECOVERY = 0.78;
export const AUTO_BALANCE_INTERVAL = 12;

const order = (building, fallback) => Number.isFinite(building.buildNumber) ? building.buildNumber : fallback;

export function balanceProduction(state, buildings, resources, totalTime) {
  const eligible = new Set(buildings.filter(b => b.progress >= 1 && b.active !== false && b.autoControl !== false).map(b => b.id));
  const idle = new Set((state._autoIdleIds || []).filter(id => eligible.has(id)));
  let lastChange = Number(state._autoControlLastAt || 0);
  if (totalTime - lastChange < AUTO_BALANCE_INTERVAL) return { idle, lastChange };

  for (const key of TRACKED) {
    const resource = resources[key];
    if (!resource || !Number.isFinite(resource.max) || resource.max <= 0) continue;
    const fill = resource.stored / resource.max;
    const netRate = Number(state.netRates?.[key] || 0) / Math.max(1, Number(state.gameSpeed || 1));
    const groups = new Map();
    buildings.forEach((b, index) => {
      if (!eligible.has(b.id)) return;
      const def = EM2C_BUILDINGS[b.type];
      if (!def || def.recipeBuilding || !def.productionOutputs?.[key]) return;
      const rate = Number(def.productionOutputs[key] || 0) * (SLOWED.has(key) ? 0.01 : 1);
      if (rate <= 0) return;
      if (!groups.has(b.type)) groups.set(b.type, []);
      groups.get(b.type).push({ b, rate, order: order(b, index + 1) });
    });

    // Recovery is considered first. Start the oldest paused module, one at a time.
    if (fill <= LOWER || (fill < RECOVERY && netRate < -0.005)) {
      const candidates = [...groups.values()].flat()
        .filter(x => idle.has(x.b.id))
        .sort((a, b) => a.order - b.order);
      if (candidates.length) {
        idle.delete(candidates[0].b.id);
        lastChange = totalTime;
        break;
      }
    }

    // When reserves are healthy, pause only a surplus module. Keep at least
    // one producer per resource and preserve a positive projected net rate.
    if (fill >= UPPER && netRate > 0.005) {
      const candidates = [...groups.values()].flat()
        .filter(x => !idle.has(x.b.id))
        .filter(x => netRate - x.rate > 0.005)
        .filter(x => {
          const allForType = groups.get(x.b.type);
          return allForType.some(other => other.b.id !== x.b.id && !idle.has(other.b.id));
        })
        .sort((a, b) => b.order - a.order);
      if (candidates.length) {
        idle.add(candidates[0].b.id);
        lastChange = totalTime;
        break;
      }
    }
  }
  return { idle, lastChange };
}
